import type {
  ProductVariantsFragment,
  ConfigurableVariantFragment
} from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';

/**
 * Creates valid options depending on the current selection.
 * Valid options are determined based on the previous valid options.
 *
 * @param optionSelection
 * @param currentSelection
 * @param optionsHierarchy A sorted list used for creation of valid options
 */
export function createValidOptions(
  optionSelection: ProductVariantsFragment['variants'],
  currentSelection: Record<string, string> = {},
  optionsHierarchy: ReadonlyArray<string>
): Record<string, string[]> {
  const variants = optionSelection?.filter(isDefined);
  if (!variants) {
    return {};
  }

  const validOptions: Record<string, string[]> = {};

  optionsHierarchy.forEach((code, index) => {
    const relevantCodes = optionsHierarchy.slice(0, index);
    const currentLevelSelection = relevantCodes
      .map((code) => currentSelection[code])
      .filter(isDefined);

    variants
      .filter((variant) =>
        matchesCurrentSelection(currentLevelSelection, variant)
      )
      .forEach((variant) => {
        variant.attributes?.forEach((attribute) => {
          if (attribute?.code !== code) {
            return;
          }

          validOptions[attribute.code] ??= [];
          if (!validOptions[attribute.code].includes(attribute.uid)) {
            validOptions[attribute.code].push(attribute.uid);
          }
        });
      });
  });

  return validOptions;
}

function matchesCurrentSelection(
  currentUids: ReadonlyArray<string>,
  variant: ConfigurableVariantFragment | null | undefined
) {
  if (!variant) {
    return false;
  }

  return currentUids.every((uid) =>
    variant.attributes?.some((attribute) => attribute?.uid === uid)
  );
}
